import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { useDayforceJobsIssuesFiltersQueryState } from '../hooks/useDayforceJobsIssuesFiltersQueryState'
import { useDayforceAgenciesContextValue } from '../../shared/context/DayforceAgenciesProvider'
import { DayforceAgency } from 'api/admin/dayforce/listDayforceAgencies'
import { useDayforceJobsIssuesQuery } from 'queries/admin/dayforce/useDayforceJobsIssuesQuery'

type DayforceJobsIssuesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useDayforceJobsIssuesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
    agencies: Record<string, DayforceAgency>
    filters: {
      agencyId: string[] | null
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setFilters: ReturnType<typeof useDayforceJobsIssuesFiltersQueryState>[1]
  }
}

const [Context, useDayforceJobsIssuesContextValue] =
  createContext<DayforceJobsIssuesContextState>({
    name: 'DayforceJobsIssuesContext',
  })

export const DayforceJobsIssuesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const {
    state: { agencies },
  } = useDayforceAgenciesContextValue()

  const [pagination, setPagination] = usePaginationQueryState()
  const [filters, setFilters] = useDayforceJobsIssuesFiltersQueryState()

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useDayforceJobsIssuesQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    agencyId: filters.agencyId,
  })

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    // eslint-disable-next-line no-console
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination,
      agencies,
      filters,
    },
    actions: {
      refetch,
      setPagination,
      setFilters,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useDayforceJobsIssuesContextValue }
